import { useEffect, useRef, useState } from "react";
import { Shield, Lock, Award, Trophy, Check, ArrowRight } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const NetProceedsCalculator = () => {
  const calculatorRef = useRef<HTMLDivElement>(null);
  const [calcInView, setCalcInView] = useState(false);

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      const d = (e.data || {}) as { source?: string; type?: string; height?: number };
      if (d.source === "net-proceeds-widget" && d.type === "truevalue:resize") {
        const f = document.getElementById("truevalue-net-proceeds") as HTMLIFrameElement | null;
        if (f && d.height) f.style.height = d.height + "px";
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && (window as any).fbq) {
      (window as any).fbq("track", "PageView");
    }
    if (typeof window !== "undefined" && (window as any).gtag) {
      (window as any).gtag("event", "page_view", { page_title: "Net Proceeds Calculator" });
    }
  }, []);

  useEffect(() => {
    const el = calculatorRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setCalcInView(entry.isIntersecting),
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const scrollToCalculator = () => {
    calculatorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const benefits = [
    "Instant estimate of what you'll walk away with",
    "Accounts for taxes, fees and deal structure",
    "No obligation — your figures stay private",
  ];

  const faqs = [
    {
      q: "What is a net proceeds estimate?",
      a: "It's the approximate amount you could receive after selling your business, once taxes, professional fees, and any outstanding liabilities are deducted from the sale price.",
    },
    {
      q: "Is this calculator free to use?",
      a: "Yes, completely free. There is no obligation and we don't ask for payment details. The estimate is generated using your inputs and typical deal assumptions.",
    },
    {
      q: "How accurate is the estimate?",
      a: "The calculator gives a useful indicative figure based on the information you provide. For a precise, tailored valuation and net proceeds breakdown, book a strategy call with one of our advisors.",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Net Proceeds Calculator | VAMOS M&A Advisory"
        description="Estimate how much you could walk away with when you sell your business. Free, instant and confidential net proceeds calculator."
        canonical="/net-proceeds-calculator"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }}
      />
      <Navigation />

      {/* Headline + Benefits + Trust + Calculator */}
      <section className="pt-20 md:pt-24 pb-6 md:pb-12 bg-background">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h1 className="text-2xl md:text-5xl font-bold text-foreground mb-3 md:mb-4 leading-tight">
            Estimate Your <span className="text-accent">Net Proceeds</span>
          </h1>
          <p className="text-sm md:text-lg text-muted-foreground mb-4 md:mb-5">
            See what you could walk away with after fees, tax and deal structure — in under 2 minutes.
          </p>

          {/* Benefit bullets */}
          <div className="flex flex-col items-center gap-1.5 md:gap-2 mb-4 md:mb-6">
            {benefits.map((b) => (
              <div key={b} className="flex items-center gap-2 text-sm md:text-base text-muted-foreground">
                <Check className="w-4 h-4 text-accent flex-shrink-0" />
                <span>{b}</span>
              </div>
            ))}
          </div>

          {/* Trust Bar */}
          <div className="w-full rounded-lg bg-[#1a1a1a] py-3 md:py-4 px-4 mb-4 md:mb-6">
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 md:gap-x-6 text-center">
              <span className="text-xs md:text-sm font-semibold text-white">£180M+ Transacted</span>
              <span className="hidden sm:inline text-white/30">|</span>
              <span className="text-xs md:text-sm font-semibold text-white">61 Days Avg to Heads of Terms</span>
              <span className="hidden sm:inline text-white/30">|</span>
              <span className="text-xs md:text-sm font-semibold text-white">Confidential Process</span>
              <span className="hidden sm:inline text-white/30">|</span>
              <span className="text-xs md:text-sm font-semibold text-white">£10k Refund Promise</span>
            </div>
          </div>

          {/* Calculator iframe */}
          <div className="flex justify-center" ref={calculatorRef}>
            <iframe
              id="truevalue-net-proceeds"
              src="https://www.truevalue.business/public/net-proceeds/06657373-ee7a-491c-9156-8f70f311a53e?embed=true"
              style={{
                width: "100%",
                border: 0,
                minHeight: "720px",
                maxWidth: "720px",
                borderRadius: "12px",
                background: "transparent",
              }}
              scrolling="no"
              allow="clipboard-write"
              title="Net Proceeds Calculator"
            />
          </div>
        </div>
      </section>

      {/* Awards Strip */}
      <section className="py-4 md:py-6 bg-background border-y border-border">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
            <div className="flex items-center gap-3 justify-center p-3 md:p-4 rounded-lg bg-secondary">
              <Trophy className="w-5 h-5 md:w-6 md:h-6 text-accent flex-shrink-0" />
              <div>
                <div className="text-xs md:text-sm font-semibold text-foreground">Business Broker of the Year</div>
                <div className="text-[10px] md:text-xs text-muted-foreground">5D Awards</div>
              </div>
            </div>
            <div className="flex items-center gap-3 justify-center p-3 md:p-4 rounded-lg bg-secondary">
              <Award className="w-5 h-5 md:w-6 md:h-6 text-accent flex-shrink-0" />
              <div>
                <div className="text-xs md:text-sm font-semibold text-foreground">Business Transfer Agency of the Year</div>
                <div className="text-[10px] md:text-xs text-muted-foreground">SME News</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Secondary CTA */}
      <section className="py-8 md:py-12 bg-background">
        <div className="max-w-2xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-xl md:text-3xl font-bold text-foreground mb-3">
            Want a precise valuation of your business?
          </h2>
          <p className="text-sm md:text-base text-muted-foreground mb-5">
            Speak with an award-winning M&A adviser. No obligation, completely confidential.
          </p>
          <Button
            asChild
            className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold"
            size="lg"
          >
            <a href="/lead-capture">
              Book a Strategy Call
              <ArrowRight className="w-4 h-4 ml-2" />
            </a>
          </Button>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-6 md:py-10 bg-secondary/50 border-y border-border">
        <div className="max-w-2xl mx-auto px-4 md:px-6">
          <h2 className="text-lg md:text-2xl font-bold text-foreground mb-4 text-center">
            Common Questions
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger className="text-sm md:text-base text-left">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Trust Pillars */}
      <section className="py-6 md:py-8 bg-secondary border-y border-border">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            <div className="flex items-center gap-2 md:gap-3 justify-center">
              <Award className="w-4 h-4 md:w-5 md:h-5 text-accent flex-shrink-0" />
              <div>
                <div className="text-xs md:text-sm font-semibold text-foreground">Qualified Valuers</div>
                <div className="text-[10px] md:text-xs text-muted-foreground">Defensible, evidence-based</div>
              </div>
            </div>
            <div className="flex items-center gap-2 md:gap-3 justify-center">
              <Lock className="w-4 h-4 md:w-5 md:h-5 text-accent flex-shrink-0" />
              <div>
                <div className="text-xs md:text-sm font-semibold text-foreground">100% Confidential</div>
                <div className="text-[10px] md:text-xs text-muted-foreground">NDA Protected Process</div>
              </div>
            </div>
            <div className="flex items-center gap-2 md:gap-3 justify-center">
              <Trophy className="w-4 h-4 md:w-5 md:h-5 text-accent flex-shrink-0" />
              <div>
                <div className="text-xs md:text-sm font-semibold text-foreground">Award Winning</div>
                <div className="text-[10px] md:text-xs text-muted-foreground">5D & SME News</div>
              </div>
            </div>
            <div className="flex items-center gap-2 md:gap-3 justify-center">
              <Shield className="w-4 h-4 md:w-5 md:h-5 text-accent flex-shrink-0" />
              <div>
                <div className="text-xs md:text-sm font-semibold text-foreground">GDPR Compliant</div>
                <div className="text-[10px] md:text-xs text-muted-foreground">ICO Registered</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Sticky mobile CTA */}
      {!calcInView && (
        <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-background/95 backdrop-blur-sm border-t border-border p-3 animate-in slide-in-from-bottom-4">
          <Button
            onClick={scrollToCalculator}
            className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-semibold"
            size="lg"
          >
            Calculate Net Proceeds
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default NetProceedsCalculator;
